import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  Euro, Users, Package, Building2, Loader2, TrendingUp, TrendingDown,
  Calendar, ChevronDown, ChevronUp, Clock, BarChart3, Truck, Hammer
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#14b8a6'];

const AnalisiCosti = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expandedCant, setExpandedCant] = useState(null);
  const [expandedWorker, setExpandedWorker] = useState(null);
  const [quickPeriod, setQuickPeriod] = useState('mese');
  const pageRef = useRef(null);

  const setPeriod = (p) => {
    setQuickPeriod(p);
    const now = new Date();
    let s, e = now.toISOString().split('T')[0];
    if (p === 'mese') s = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    else if (p === 'mese_prec') { s = new Date(now.getFullYear(), now.getMonth()-1, 1).toISOString().split('T')[0]; e = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0]; }
    else if (p === '90gg') s = new Date(Date.now()-90*86400000).toISOString().split('T')[0];
    else if (p === 'anno') s = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
    setStartDate(s); setEndDate(e);
  };

  useEffect(() => { setLoading(true); api.getAnalisiCosti(startDate, endDate).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false)); }, [startDate, endDate]);

  if (loading || !data) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  const pieCategorie = [
    { name: 'Persone', value: data.totale_persone, icon: Users, color: '#3b82f6' },
    { name: 'Materiali', value: data.totale_materiali, icon: Package, color: '#22c55e' },
  ].filter(d => d.value > 0);

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Analisi Costi" subtitle={`${new Date(startDate+'T12:00').toLocaleDateString('it-IT')} — ${new Date(endDate+'T12:00').toLocaleDateString('it-IT')}`} />
      <div className="page-header">
        <div className="header-left"><h1 className="page-title">Analisi Costi</h1><p className="page-subtitle">Dashboard interattiva — clicca per esplorare</p></div>
        <div className="header-right"><SectionActions sectionTitle="Analisi Costi" contentRef={pageRef} /></div>
      </div>

      {/* Filtri */}
      <div className="ac-filter">
        <div className="ac-periods">
          {[['mese','Mese Corrente'],['mese_prec','Mese Prec.'],['90gg','90 Giorni'],['anno','Anno']].map(([k,l]) => (
            <button key={k} className={`filter-chip ${quickPeriod === k ? 'filter-chip-active' : ''}`} onClick={() => setPeriod(k)}>{l}</button>
          ))}
        </div>
        <div className="ac-custom-dates">
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setQuickPeriod(''); }} className="date-input" />
          <span>—</span>
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setQuickPeriod(''); }} className="date-input" />
        </div>
      </div>

      {/* KPI */}
      <div className="ac-kpis">
        <div className="ac-kpi ac-kpi-total"><Euro size={24} /><span className="ac-kpi-value">€{data.totale_generale.toFixed(2)}</span><span className="ac-kpi-label">Costi Totali</span></div>
        <div className="ac-kpi ac-kpi-people"><Users size={24} /><span className="ac-kpi-value">€{data.totale_persone.toFixed(2)}</span><span className="ac-kpi-label">Persone</span></div>
        <div className="ac-kpi ac-kpi-mat"><Package size={24} /><span className="ac-kpi-value">€{data.totale_materiali.toFixed(2)}</span><span className="ac-kpi-label">Materiali</span></div>
        <div className="ac-kpi ac-kpi-cant"><Building2 size={24} /><span className="ac-kpi-value">{data.per_cantiere.length}</span><span className="ac-kpi-label">Cantieri</span></div>
      </div>

      {/* Grafici Row */}
      <div className="ac-charts-row">
        <div className="ac-chart-card" style={{ flex: 2 }}>
          <h3><BarChart3 size={14} /> Trend Costi Ultimi 6 Mesi</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.trend_mensile}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="mese" tick={{ fontSize: 11, fill: '#9ca3af' }} /><YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => `€${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} /><Tooltip formatter={v => `€${v.toFixed(2)}`} /><Bar dataKey="totale" fill="#3b82f6" radius={[6,6,0,0]} name="Costo" /></BarChart>
          </ResponsiveContainer>
        </div>
        {pieCategorie.length > 0 && (
          <div className="ac-chart-card" style={{ flex: 1 }}>
            <h3>Costi per Categoria</h3>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart><Pie data={pieCategorie} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={4} dataKey="value">{pieCategorie.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}</Pie><Tooltip formatter={v => `€${v.toFixed(2)}`} /></PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 10px' }}>
              {pieCategorie.map((d, i) => {
                const pct = data.totale_generale > 0 ? ((d.value / data.totale_generale) * 100).toFixed(0) : 0;
                return <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{d.name}</span>
                  <strong>€{d.value.toFixed(2)}</strong>
                  <span style={{ color: '#9ca3af' }}>({pct}%)</span>
                </div>;
              })}
            </div>
          </div>
        )}
      </div>

      {/* CANTIERI CON DRILL-DOWN */}
      <div className="ac-section">
        <h3 className="ac-section-title"><Building2 size={16} /> Costi per Cantiere — Clicca per dettagli</h3>
        <div className="ac-cantieri-list">
          {data.per_cantiere.map((c, i) => {
            const overBudget = c.preventivo > 0 && c.totale > c.preventivo;
            const pct = c.preventivo > 0 ? Math.min(100, (c.totale / c.preventivo) * 100) : 0;
            const isExp = expandedCant === c.id;
            const cantWorkers = data.per_operaio.filter(o => o.cantieri?.some(cn => cn === c.name));
            return (
              <div key={c.id} className={`ac-cant-wrap ${isExp ? 'ac-cant-expanded' : ''}`}>
                <div className={`ac-cant-card ${overBudget ? 'ac-cant-over' : c.preventivo > 0 ? 'ac-cant-ok' : ''}`} onClick={() => setExpandedCant(isExp ? null : c.id)}>
                  <div className="ac-cant-left">
                    <div className="ac-cant-num" style={{ background: COLORS[i%COLORS.length]+'20', color: COLORS[i%COLORS.length] }}>{i+1}</div>
                    <div><h4>{c.name}</h4><p>{c.num_operai} operai • {c.num_materiali} materiali • {c.ore}h</p></div>
                  </div>
                  <div className="ac-cant-costs">
                    <div className="ac-cant-cost"><Users size={12} color="#3b82f6" /> €{c.costo_persone.toFixed(2)}</div>
                    <div className="ac-cant-cost"><Package size={12} color="#22c55e" /> €{c.costo_materiali.toFixed(2)}</div>
                  </div>
                  <div className="ac-cant-total">
                    <span className="ac-cant-amount">€{c.totale.toFixed(2)}</span>
                    {c.preventivo > 0 && (
                      <div className="ac-cant-budget">
                        <div className="ac-budget-bar"><div className="ac-budget-fill" style={{ width: `${pct}%`, background: pct > 90 ? '#ef4444' : pct > 70 ? '#f59e0b' : '#22c55e' }} /></div>
                        <span style={{ color: overBudget ? '#ef4444' : '#22c55e', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
                          {overBudget ? <><TrendingDown size={12} /> SOPRA</> : <><TrendingUp size={12} /> OK</>} {pct.toFixed(0)}% di €{c.preventivo.toFixed(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  {isExp ? <ChevronUp size={16} color="#9ca3af" /> : <ChevronDown size={16} color="#9ca3af" />}
                </div>

                {/* DRILL-DOWN CANTIERE */}
                {isExp && (
                  <div className="ac-drill">
                    <div className="ac-drill-section">
                      <h5><Users size={13} /> Operai su questo cantiere</h5>
                      {cantWorkers.length > 0 ? cantWorkers.map(w => (
                        <div key={w.id} className="ac-drill-row">
                          <span className="ac-dr-name">{w.name}</span>
                          <span className="ac-dr-role">{w.role}</span>
                          <span className="ac-dr-hours"><Clock size={11} /> {w.ore_totali}h</span>
                          <span className="ac-dr-cost">€{w.costo_totale.toFixed(2)}</span>
                        </div>
                      )) : <p className="empty-text" style={{ padding: '8px 0' }}>Nessun operaio nel periodo</p>}
                    </div>
                    <div className="ac-drill-summary">
                      <span>Persone: <strong style={{ color: '#3b82f6' }}>€{c.costo_persone.toFixed(2)}</strong></span>
                      <span>Materiali: <strong style={{ color: '#22c55e' }}>€{c.costo_materiali.toFixed(2)}</strong></span>
                      <span>TOTALE: <strong style={{ color: '#1f2937', fontSize: 16 }}>€{c.totale.toFixed(2)}</strong></span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* OPERAI CON DRILL-DOWN */}
      <div className="ac-section">
        <h3 className="ac-section-title"><Users size={16} /> Costi per Operaio — Clicca per cantieri</h3>
        <div className="ac-workers-list">
          {data.per_operaio.filter(o => o.ore_totali > 0).map(o => {
            const isExp = expandedWorker === o.id;
            return (
              <div key={o.id} className={`ac-worker-card ${isExp ? 'ac-worker-expanded' : ''}`} onClick={() => setExpandedWorker(isExp ? null : o.id)}>
                <div className="ac-worker-main">
                  <div className="ac-worker-info"><h4>{o.name}</h4><p>{o.role} — €{o.costoOrario || 0}/h</p></div>
                  <div className="ac-worker-hours">
                    <span className="ac-wh-ord"><Clock size={12} /> {o.ore_ordinarie}h</span>
                    {o.ore_extra > 0 && <span className="ac-wh-extra">+{o.ore_extra}h extra</span>}
                  </div>
                  <span className="ac-worker-cost">€{o.costo_totale.toFixed(2)}</span>
                  {isExp ? <ChevronUp size={14} color="#9ca3af" /> : <ChevronDown size={14} color="#9ca3af" />}
                </div>
                {isExp && o.cantieri.length > 0 && (
                  <div className="ac-worker-detail">
                    <span className="ac-wd-label">Ha lavorato su:</span>
                    {o.cantieri.map((cn, i) => <span key={i} className="ac-wd-cant" onClick={(e) => { e.stopPropagation(); const c = data.per_cantiere.find(x => x.name === cn); if (c) setExpandedCant(c.id); }}>{cn}</span>)}
                  </div>
                )}
              </div>
            );
          })}
          {data.per_operaio.filter(o => o.ore_totali > 0).length === 0 && <p className="empty-text">Nessun costo operai nel periodo</p>}
        </div>
      </div>

      {/* Grafico Cantieri */}
      {data.per_cantiere.some(c => c.totale > 0) && (
        <div className="ac-section"><div className="ac-chart-card">
          <h3>Persone vs Materiali per Cantiere</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.per_cantiere.map(c => ({ name: c.name.length > 15 ? c.name.slice(0,15)+'...' : c.name, Persone: c.costo_persone, Materiali: c.costo_materiali }))}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} /><YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => `€${v}`} /><Tooltip formatter={v => `€${v.toFixed(2)}`} /><Bar dataKey="Persone" fill="#3b82f6" radius={[4,4,0,0]} /><Bar dataKey="Materiali" fill="#22c55e" radius={[4,4,0,0]} /></BarChart>
          </ResponsiveContainer>
        </div></div>
      )}
    </div>
  );
};

export default AnalisiCosti;
